'use client';

import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import dynamic from 'next/dynamic';
import { useEffect, useRef, useState, type CSSProperties } from 'react';

import {
  FLYWAYS_MAP_ARTBOARD_ASPECT_RATIO,
  FLYWAYS_MAP_ASPECT_RATIO,
  FLYWAYS_MAP_VIEWPORT_ROOT_MARGIN,
  type FlywaysMapDir,
  type FlywaysMapFilterId,
} from '@/constants/flywaysMap';
import { useInView } from '@/hooks/useInView';
import { loadFlywaysMapSvg } from '@/lib/flywaysMapSvg';
import { installFlywaysPerfBenchWhenEnabled } from '@/lib/perfLab';
import { durations, easeStandard } from '@/motion/tokens';

import styles from './FlywaysMap.module.css';
import { useFlywaysMapGestures } from './useFlywaysMapGestures';

import type { Messages } from '@/content/messages';

type FlywaysMapIslandProps = {
  filter: FlywaysMapFilterId;
  messages: Messages['intro']['flywaysMap'];
  caption: string;
  dir: FlywaysMapDir;
  /** Compact (`< lg`): full-bleed plate, pan/pinch, mobile cameras, no badges. */
  compact?: boolean;
};

const FlywaysMapInteractiveDynamic = dynamic(
  () =>
    import('./FlywaysMapInteractive').then((mod) => mod.FlywaysMapInteractive),
  { ssr: false, loading: () => null },
);

const TIP_MOTION = { duration: durations.fast, ease: easeStandard };

/**
 * Viewport-gated client boundary for the GSAP flyways map island.
 * Owns an empty `.svgHost` plate (aspect-ratio reserved). Prefetches the SVG
 * and mounts interactive enhance once (`once: true`) when near the viewport;
 * CSS `.preview` keeps SELECTED layers hidden until ready.
 */
export function FlywaysMapIsland({
  filter,
  messages,
  caption,
  dir,
  compact = false,
}: FlywaysMapIslandProps) {
  const { ref, inView } = useInView<HTMLDivElement>({
    once: true,
    margin: FLYWAYS_MAP_VIEWPORT_ROOT_MARGIN,
  });
  const hostRef = useRef<HTMLDivElement>(null);
  const cameraViewportRef = useRef<HTMLDivElement>(null);
  const cameraLayerRef = useRef<HTMLDivElement>(null);
  const gestureHostRef = useRef<HTMLDivElement>(null);
  const gestureLayerRef = useRef<HTMLDivElement>(null);
  const [isPreview, setIsPreview] = useState(true);
  const reducedMotion = useReducedMotion();
  const {
    reset: resetGestures,
    showTwoFingerTip,
    handlers,
    style: gestureStyle,
  } = useFlywaysMapGestures({
    enabled: compact,
    hostRef: gestureHostRef,
    layerRef: gestureLayerRef,
  });

  useEffect(() => {
    installFlywaysPerfBenchWhenEnabled();
  }, []);

  // Prefetch SVG as soon as the plate nears the viewport (shared cache with Interactive).
  useEffect(() => {
    if (!inView) return;
    void loadFlywaysMapSvg();
  }, [inView]);

  useEffect(() => {
    if (!compact) return;
    resetGestures();
  }, [filter, compact, resetGestures]);

  const viewportClass = [
    styles.viewport,
    isPreview ? styles.preview : null,
    compact ? styles.viewportCompact : null,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={ref}
      className={viewportClass}
      style={compact ? undefined : { aspectRatio: String(FLYWAYS_MAP_ASPECT_RATIO) }}
      role={isPreview ? 'img' : undefined}
      aria-label={isPreview ? messages.ariaLabel : undefined}
    >
      <div
        ref={gestureHostRef}
        className={styles.gestureHost}
        style={gestureStyle}
        {...(compact ? handlers : {})}
      >
        <div ref={gestureLayerRef} className={styles.gestureLayer}>
          <div
            ref={cameraViewportRef}
            className={styles.camera}
            style={
              {
                '--flyways-artboard-aspect': String(
                  FLYWAYS_MAP_ARTBOARD_ASPECT_RATIO,
                ),
              } as CSSProperties
            }
          >
            {/* Artboard cover plate — GSAP CSS camera target (not `.camera`). */}
            <div ref={cameraLayerRef} className={styles.plate}>
              <div ref={hostRef} className={styles.svgHost} />
            </div>
          </div>
        </div>
      </div>
      {compact ? (
        <AnimatePresence>
          {showTwoFingerTip ? (
            <motion.p
              key="two-finger-tip"
              className={styles.twoFingerTip}
              role="status"
              initial={reducedMotion ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={reducedMotion ? undefined : { opacity: 0 }}
              transition={reducedMotion ? { duration: 0 } : TIP_MOTION}
            >
              {messages.twoFingerPanTip}
            </motion.p>
          ) : null}
        </AnimatePresence>
      ) : null}
      {inView ? (
        <FlywaysMapInteractiveDynamic
          hostRef={hostRef}
          cameraViewportRef={cameraViewportRef}
          cameraLayerRef={cameraLayerRef}
          filter={filter}
          messages={messages}
          caption={caption}
          dir={dir}
          compact={compact}
          onReady={() => setIsPreview(false)}
        />
      ) : null}
    </div>
  );
}
